

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useSocket } from "../context/SocketProvider";
import peer from "../service/peer";
import OtpPanel from "./OtpPanel";
import "./Room.css";

function decodeJwtRole(token) {
  try {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json.role || json.userType || null;
  } catch { return null; }
}

function decodeJwtUserId(token) {
  try {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json.userId != null ? String(json.userId) : null;
  } catch { return null; }
}

export default function Room() {
  const { roomId }              = useParams();
  const [searchParams]          = useSearchParams();
  const navigate                = useNavigate();
  const { socket, isConnected } = useSocket();

  const localVideo  = useRef(null);
  const remoteVideo = useRef(null);
  const pendingRemoteStreamRef = useRef(null);
  const remoteVideoMounted     = useRef(false);

  // FIX D1: ICE candidate buffer — holds candidates that arrive before
  // remoteDescription is set. Drained by drainIceCandidates() after setAnswer/createAnswer.
  const iceCandidateBuffer = useRef([]);
  const remoteDescSet      = useRef(false);

  // FIX D3: dedupe guard — tracks the peerId we most recently built a PC for
  const currentPeerIdRef = useRef(null);

  const [localStream,           setLocalStream]           = useState(null);
  const [isCameraOn,            setIsCameraOn]            = useState(true);
  const [isMicOn,               setIsMicOn]               = useState(true);
  const [messages,              setMessages]              = useState([]);
  const [messageInput,          setMessageInput]          = useState("");
  const [isChatOpen,            setIsChatOpen]            = useState(false);
  const [error,                 setError]                 = useState("");
  const [callStatus,            setCallStatus]            = useState("waiting");
  const [rejectedReason,        setRejectedReason]        = useState(null);
  const [timeRemaining,         setTimeRemaining]         = useState(null);
  const [slotExpired,           setSlotExpired]           = useState(false);
  const [unreadCount,           setUnreadCount]           = useState(0);
  const [showEndConfirm,        setShowEndConfirm]        = useState(false);
  const [peerLeftMessage,       setPeerLeftMessage]       = useState(null);
  const [showRejoinPrompt,      setShowRejoinPrompt]      = useState(false);
  const [needsMediaUnblock,     setNeedsMediaUnblock]     = useState(false);
  const [mediaPermissionDenied, setMediaPermissionDenied] = useState(false);
  const [isOtpPanelOpen,        setIsOtpPanelOpen]        = useState(false);
  const [keyboardOffset,        setKeyboardOffset]        = useState(0);
  const unblockingRef = useRef(false);

  // ── URL / sessionStorage params ────────────────────────────────────────────
  const urlUserId     = searchParams.get("userId");
  const urlUserType   = searchParams.get("userType");
  const token         = searchParams.get("token")         || sessionStorage.getItem("video_token")     || "";
  const slotStartTime = searchParams.get("slotStartTime") || sessionStorage.getItem("slot_start_time") || null;
  const slotEndTime   = searchParams.get("slotEndTime")   || sessionStorage.getItem("slot_end_time")   || null;
  const appointmentId =
    searchParams.get("appointmentId") ||
    sessionStorage.getItem("appointment_id") ||
    (roomId?.startsWith("room_") ? roomId.replace("room_", "") : null);

  const jwtRole   = useMemo(() => decodeJwtRole(token),   [token]);
  const jwtUserId = useMemo(() => decodeJwtUserId(token), [token]);

  const myRole = useMemo(() => {
    const resolved = jwtRole || urlUserType || sessionStorage.getItem("user_type") || "guest";
    if (jwtRole && urlUserType && jwtRole !== urlUserType)
      console.warn(`⚠️ Role mismatch: JWT="${jwtRole}", URL="${urlUserType}". Using JWT.`);
    return resolved;
  }, [jwtRole, urlUserType]);

  const myUserId = useMemo(() =>
    jwtUserId || urlUserId || sessionStorage.getItem("user_id") || `user_${Date.now()}`,
  [jwtUserId, urlUserId]);

  const localStreamRef = useRef(null);
  useEffect(() => { localStreamRef.current = localStream; }, [localStream]);

  // ── Keyboard-aware layout (WebView) ───────────────────────────────────────
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const shift = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(kb);
      const bar = document.querySelector(".controls");
      if (bar) bar.style.transform = `translateY(-${kb}px)`;
      const status = document.querySelector(".status-bar");
      if (status) status.style.bottom = kb > 0 ? `${kb + 8}px` : "";
    };
    vv.addEventListener("resize", shift);
    vv.addEventListener("scroll", shift);
    return () => { vv.removeEventListener("resize", shift); vv.removeEventListener("scroll", shift); };
  }, []);

  // ── FIX D1: Drain buffered ICE candidates after remote description is set ──
  const drainIceCandidates = useCallback(async () => {
    if (!peer.pc || !remoteDescSet.current) return;
    const buffered = iceCandidateBuffer.current.splice(0);
    if (buffered.length > 0) {
      console.log(`🧊 Draining ${buffered.length} buffered ICE candidate(s)`);
    }
    for (const candidate of buffered) {
      try {
        await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn("⚠️ Buffered ICE candidate error:", e.message);
      }
    }
  }, []);

  // ── Get local media ─────────────────────────────────────────────────────────
  const getLocalStream = useCallback(async () => {
    if (localStreamRef.current?.active) {
      if (localVideo.current && !localVideo.current.srcObject) {
        localVideo.current.srcObject = localStreamRef.current;
      }
      return localStreamRef.current;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      if (localVideo.current) localVideo.current.srcObject = stream;
      setLocalStream(stream);
      localStreamRef.current = stream;
      setMediaPermissionDenied(false);
      console.log("🎥 Local stream acquired");
      return stream;
    } catch (err) {
      console.error("Media error:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMediaPermissionDenied(true);
        setError("Camera & microphone access was denied. Please allow access in your browser settings and refresh.");
      } else {
        setError("Could not access camera or microphone. Please check your device.");
      }
      return null;
    }
  }, []);

  // ── FIX 1: Start camera on mount ───────────────────────────────────────────
  useEffect(() => {
    getLocalStream();
    return () => {
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── FIX C: ref callback for remote video element ───────────────────────────
  const setRemoteVideoRef = useCallback((el) => {
    remoteVideo.current = el;
    if (!el) { remoteVideoMounted.current = false; return; }
    remoteVideoMounted.current = true;

    const stream = pendingRemoteStreamRef.current;
    if (stream) {
      el.srcObject = stream;
      el.muted = false;
      el.play().then(() => {
        setNeedsMediaUnblock(false);
      }).catch((err) => {
        console.warn("Autoplay blocked on ref attach:", err.name);
        setNeedsMediaUnblock(true);
      });
    }
  }, []);

  // ── End call ────────────────────────────────────────────────────────────────
  const endCall = useCallback((reason) => {
    setCallStatus("ended");
    setShowEndConfirm(false);
    setShowRejoinPrompt(false);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    setLocalStream(null);
    localStreamRef.current = null;
    pendingRemoteStreamRef.current = null;
    iceCandidateBuffer.current = [];
    remoteDescSet.current = false;
    currentPeerIdRef.current = null;
    peer.close();
    socket?.emit("call:end", { roomId });
    ["video_token","slot_start_time","slot_end_time","room_id","user_id","user_type"]
      .forEach((k) => sessionStorage.removeItem(k));
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "END_CALL", reason: reason || "user_ended" })
      );
    } else {
      navigate("/");
    }
  }, [socket, roomId, navigate]);

  const endCallRef = useRef(endCall);
  useEffect(() => { endCallRef.current = endCall; }, [endCall]);

  // ── Countdown ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!slotEndTime) return;
    const endDate = new Date(slotEndTime);
    let fired = false;
    const tick = () => {
      const diffMs = endDate - new Date();
      if (diffMs <= 0) {
        setTimeRemaining("00:00");
        if (!fired) { fired = true; setSlotExpired(true); setTimeout(() => endCallRef.current("slot_ended"), 3000); }
        return;
      }
      const t = Math.floor(diffMs / 1000);
      const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
      setTimeRemaining(h > 0
        ? `${h}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`
        : `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [slotEndTime]);

  // ── FIX B: Media unblock — works on first tap ──────────────────────────────
  const handleMediaUnblock = useCallback(async () => {
    if (unblockingRef.current) return;
    unblockingRef.current = true;
    setNeedsMediaUnblock(false);
    try {
      const stream = pendingRemoteStreamRef.current;
      if (stream && remoteVideo.current) {
        remoteVideo.current.srcObject = stream;
        remoteVideo.current.muted = false;
        try {
          await remoteVideo.current.play();
        } catch {
          remoteVideo.current.muted = true;
          await remoteVideo.current.play().catch(() => {});
          remoteVideo.current.muted = false;
        }
      }
      if (!localStreamRef.current?.active) await getLocalStream();
    } catch (err) {
      console.error("Media unblock error:", err);
    } finally {
      unblockingRef.current = false;
    }
  }, [getLocalStream]);

  // ── FIX A: Toggles — also mute the WebRTC sender ──────────────────────────
  const toggleCamera = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const newEnabled = !track.enabled;
    track.enabled = newEnabled;
    if (peer.pc) {
      peer.pc.getSenders().forEach((sender) => {
        if (sender.track?.kind === "video") sender.track.enabled = newEnabled;
      });
    }
    setIsCameraOn(newEnabled);
    socket?.emit("call:mute", { roomId, audio: isMicOn, video: newEnabled });
  }, [socket, roomId, isMicOn]);

  const toggleMic = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    const newEnabled = !track.enabled;
    track.enabled = newEnabled;
    if (peer.pc) {
      peer.pc.getSenders().forEach((sender) => {
        if (sender.track?.kind === "audio") sender.track.enabled = newEnabled;
      });
    }
    setIsMicOn(newEnabled);
    socket?.emit("call:mute", { roomId, audio: newEnabled, video: isCameraOn });
  }, [socket, roomId, isCameraOn]);

  // ── Chat ────────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (isChatOpen) { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); setUnreadCount(0); }
  }, [messages, isChatOpen]);

  const handleChatMessage = useCallback(({ message }) => {
    setMessages((prev) => prev.some((m) => m.id === message.id) ? prev : [...prev, message]);
    setUnreadCount((c) => isChatOpen ? 0 : c + 1);
  }, [isChatOpen]);

  const sendMessage = () => {
    if (!messageInput.trim() || !socket) return;
    socket.emit("chat:message", {
      roomId,
      message: {
        id: `${myUserId}-${Date.now()}`, sender: myUserId, senderType: myRole,
        text: messageInput.trim(), timestamp: new Date().toISOString(),
      },
    });
    setMessageInput("");
  };

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  const isMyMessage    = (msg) => msg.senderType ? msg.senderType === myRole : String(msg.sender) === String(myUserId);
  const getSenderLabel = (msg) => isMyMessage(msg) ? "You" : "CetaUser";

  // ── Join room ───────────────────────────────────────────────────────────────
  const joinRoom = useCallback(() => {
    if (!socket || !roomId) return;
    console.log(`🚪 room:join room=${roomId} userId=${myUserId} role=${myRole}`);
    socket.emit("room:join", { roomId, userId: myUserId, userType: myRole, token, slotStartTime, slotEndTime });
  }, [socket, roomId, myUserId, myRole, token, slotStartTime, slotEndTime]);

  // ── wirePc — shared handler wiring ────────────────────────────────────────
  const wirePc = useCallback((peerId) => {
    peer.pc.ontrack = (e) => {
      const stream = e.streams?.[0];
      if (!stream) return;
      console.log("🎥 ontrack fired, stream id:", stream.id);
      pendingRemoteStreamRef.current = stream;
      setCallStatus("connected");
      setPeerLeftMessage(null);
      setShowRejoinPrompt(false);

      if (remoteVideo.current) {
        remoteVideo.current.srcObject = null;
        remoteVideo.current.srcObject = stream;
        remoteVideo.current.muted = false;
        remoteVideo.current.play().then(() => {
          setNeedsMediaUnblock(false);
        }).catch((err) => {
          console.warn("Autoplay blocked:", err.name);
          setNeedsMediaUnblock(true);
        });
      } else {
        console.log("📺 remoteVideo not mounted yet, stream queued");
      }
    };

    peer.pc.onicecandidate = (e) => {
      if (e.candidate) {
        socket.emit("signal:ice", { roomId, candidate: e.candidate, to: peerId });
      }
    };

    peer.pc.onconnectionstatechange = () => {
      const state = peer.pc?.connectionState;
      console.log("🔗 PeerConnection state:", state);
      if (state === "connected")                          setCallStatus("connected");
      if (state === "failed" || state === "disconnected") setCallStatus("waiting");
    };

    peer.pc.oniceconnectionstatechange = () => {
      console.log("🧊 ICE state:", peer.pc?.iceConnectionState);
    };
  }, [socket, roomId]);

  // ── buildPeerConnection ────────────────────────────────────────────────────
  const buildPeerConnection = useCallback(async ({ peerId, isCaller }) => {
    // FIX D3: prevent double-build for the same peer in the same session
    if (currentPeerIdRef.current === peerId && peer.pc?.signalingState !== "closed") {
      console.log(`⚠️ buildPeerConnection: already built for peerId=${peerId}, skipping`);
      return;
    }
    currentPeerIdRef.current = peerId;

    // Reset ICE buffer and remoteDesc flag for the new negotiation
    iceCandidateBuffer.current = [];
    remoteDescSet.current = false;

    peer.reset();
    wirePc(peerId);

    const stream = localStreamRef.current?.active
      ? localStreamRef.current
      : await getLocalStream();

    if (stream) {
      stream.getTracks().forEach((track) => peer.pc.addTrack(track, stream));
      console.log(`📡 Added ${stream.getTracks().length} track(s) to PeerConnection`);
    } else {
      console.warn("⚠️ No local stream available");
    }

    if (isCaller) {
      const offer = await peer.createOffer();
      socket.emit("signal:offer", { roomId, offer, to: peerId });
      console.log("📤 Offer sent →", peerId);
    }
  }, [socket, roomId, getLocalStream, wirePc]);

  // ── Socket listeners ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket || !roomId) return;

    const onJoinRejected = ({ reason, message }) => {
      setRejectedReason({ reason, message });
      setCallStatus("rejected");
    };

    const onPeerReady = async ({ peerId, role }) => {
      console.log(`🤝 peer:ready peerId=${peerId} role=${role}`);
      setCallStatus("connecting");
      await buildPeerConnection({ peerId, isCaller: role === "caller" });
    };

    // FIX D2: tracks added BEFORE createAnswer, remoteDesc set flag updated AFTER
    const onOffer = async ({ offer, from }) => {
      console.log("📨 signal:offer from", from);

      // Reset ICE buffer for fresh negotiation
      iceCandidateBuffer.current = [];
      remoteDescSet.current = false;

      if (!peer.pc || peer.pc.signalingState === "closed") {
        console.log("♻️ pc closed — reinit before processing offer");
        peer.reset();
        currentPeerIdRef.current = from;
      }

      wirePc(from);

      // FIX D2: ensure local tracks are added BEFORE createAnswer
      // so the SDP answer includes our media — if this is skipped the
      // remote peer gets an answer with no tracks and ontrack never fires.
      if (peer.pc.getSenders().length === 0) {
        const stream = localStreamRef.current?.active
          ? localStreamRef.current
          : await getLocalStream();
        if (stream) {
          stream.getTracks().forEach((track) => peer.pc.addTrack(track, stream));
          console.log("📡 Added tracks (callee path)");
        } else {
          console.warn("⚠️ No local stream for callee — remote will see blank");
        }
      }

      // createAnswer internally calls setRemoteDescription(offer) then createAnswer
      const answer = await peer.createAnswer(offer);

      // Mark remote description as set BEFORE draining so candidates apply correctly
      remoteDescSet.current = true;
      await drainIceCandidates();

      socket.emit("signal:answer", { roomId, answer, to: from });
      console.log("📤 Answer sent →", from);
    };

    // FIX D1: after setAnswer, mark remoteDesc set and drain buffer
    const onAnswer = async ({ answer }) => {
      console.log("📨 signal:answer received");
      await peer.setAnswer(answer);
      remoteDescSet.current = true;
      await drainIceCandidates();
    };

    // FIX D1: buffer candidates that arrive before remoteDescription is set
    const onIce = async ({ candidate }) => {
      if (!candidate) return;

      if (!remoteDescSet.current) {
        console.log("🧊 Buffering ICE candidate (remoteDesc not set yet)");
        iceCandidateBuffer.current.push(candidate);
        return;
      }

      try {
        await peer.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn("⚠️ ICE candidate error:", e.message);
      }
    };

    const onCallEnded = ({ reason } = {}) => {
      if (reason === "slot_ended") {
        setSlotExpired(true);
        setTimeout(() => endCallRef.current(reason), 3000);
      } else {
        endCallRef.current(reason || "remote_ended");
      }
    };

    const onPeerLeft = ({ message: msg }) => {
      console.log("👋 peer:left");
      if (remoteVideo.current) {
        remoteVideo.current.pause();
        remoteVideo.current.srcObject = null;
      }
      pendingRemoteStreamRef.current = null;
      iceCandidateBuffer.current = [];
      remoteDescSet.current = false;
      currentPeerIdRef.current = null;
      peer.close();
      setCallStatus("waiting");
      setNeedsMediaUnblock(false);
      setPeerLeftMessage(msg || "The other participant has left the call.");
      setShowRejoinPrompt(true);
    };

    socket.on("join:rejected",     onJoinRejected);
    socket.on("peer:ready",        onPeerReady);
    socket.on("signal:offer",      onOffer);
    socket.on("signal:answer",     onAnswer);
    socket.on("signal:ice",        onIce);
    socket.on("chat:message",      handleChatMessage);
    socket.on("chat:history",      ({ messages: h }) => { if (Array.isArray(h)) setMessages(h); });
    socket.on("call:ended",        onCallEnded);
    socket.on("peer:waiting",      () => setCallStatus("waiting"));
    socket.on("peer:left",         onPeerLeft);
    socket.on("peer:disconnected", () => {});

    return () => {
      socket.off("join:rejected"); socket.off("peer:ready");
      socket.off("signal:offer");  socket.off("signal:answer"); socket.off("signal:ice");
      socket.off("chat:message");  socket.off("chat:history");
      socket.off("call:ended");    socket.off("peer:waiting");
      socket.off("peer:left");     socket.off("peer:disconnected");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, roomId]);

  useEffect(() => {
    if (isConnected && socket && roomId) joinRoom();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // ── Full-screen states ─────────────────────────────────────────────────────
  if (slotExpired) return (
    <div className="room fullscreen-center">
      <div style={{ fontSize: 52 }}>⏰</div>
      <h2 style={{ color: "#fff", margin: "8px 0 4px" }}>Appointment Slot Ended</h2>
      <p style={{ color: "rgba(255,255,255,0.65)", maxWidth: 280, textAlign: "center", margin: "0 0 20px" }}>
        Your appointment time has ended. Disconnecting…
      </p>
      <div className="spinner" />
    </div>
  );

  if (rejectedReason) return (
    <div className="room fullscreen-center">
      <div style={{ fontSize: 48 }}>{rejectedReason.reason === "too_early" ? "⏰" : "🚫"}</div>
      <h2 style={{ color: "#fff", margin: "8px 0 4px" }}>
        {rejectedReason.reason === "too_early" ? "Too Early to Join" : "Meeting Has Ended"}
      </h2>
      <p style={{ color: "rgba(255,255,255,0.7)", maxWidth: 300, textAlign: "center", margin: "0 0 20px" }}>
        {rejectedReason.message}
      </p>
      <button className="btn-primary" onClick={() => navigate("/")}>Go Back</button>
    </div>
  );

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="room">

      {error && (
        <div className="error-banner" role="alert">
          {error}
          <button className="error-dismiss" onClick={() => setError("")} aria-label="Dismiss">✕</button>
        </div>
      )}

      <div className="video-stage">
        <video ref={setRemoteVideoRef} autoPlay playsInline className="main-video" />
        <video ref={localVideo}        autoPlay muted playsInline className="pip-video" />
      </div>

      {timeRemaining && (
        <div className={`timer-badge${(() => {
          const p = timeRemaining.split(":");
          const m = p.length === 3 ? parseInt(p[0]) * 60 + parseInt(p[1]) : parseInt(p[0]);
          return m <= 5 ? " timer-urgent" : "";
        })()}`}>⏱ {timeRemaining}</div>
      )}

      <div className="controls">
        <button className="ctrl-btn" onClick={toggleCamera} aria-label={isCameraOn ? "Turn off camera" : "Turn on camera"}>
          {isCameraOn ? "📷" : "📵"}
        </button>
        <button className="ctrl-btn" onClick={toggleMic} aria-label={isMicOn ? "Mute" : "Unmute"}>
          {isMicOn ? "🎤" : "🔇"}
        </button>
        <button className="ctrl-btn chat-btn" aria-label="Chat"
          onClick={() => { setIsChatOpen(!isChatOpen); setUnreadCount(0); }}>
          💬
          {!isChatOpen && unreadCount > 0 && (
            <span className="unread-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
          )}
        </button>
        {appointmentId && (
          <button className="ctrl-btn"
            onClick={() => setIsOtpPanelOpen(true)}
            aria-label={myRole === "doctor" ? "Verify OTP" : "View OTP"}
            style={{ boxShadow: isOtpPanelOpen ? "0 0 0 2px rgba(0,180,216,0.8)" : "none", transition: "box-shadow 0.15s" }}>
            {myRole === "doctor" ? "🛡️" : "🔑"}
          </button>
        )}
        <button className="ctrl-btn end-btn" aria-label="Leave call" onClick={() => setShowEndConfirm(true)}>
          📞
        </button>
      </div>

      {!isConnected && (
        <div className="connecting-overlay">
          <div className="connecting-card">
            <div className="connecting-spinner" />
            <p className="connecting-text">Connecting to server…</p>
            <p className="connecting-sub">If this takes more than 10 seconds, check your connection.</p>
          </div>
        </div>
      )}

      {isConnected && callStatus === "waiting" && !showRejoinPrompt && !showEndConfirm && (
        <div className="waiting-banner">
          <span className="waiting-dot" />
          Waiting for the other participant to join…
        </div>
      )}

      {isConnected && callStatus === "connecting" && !showRejoinPrompt && !showEndConfirm && (
        <div className="waiting-banner">
          <span className="waiting-dot" style={{ background: "#00b4d8", animationDuration: "0.7s" }} />
          Connecting video…
        </div>
      )}

      {showEndConfirm && (
        <div className="end-confirm-overlay">
          <div className="end-confirm-popup">
            <div className="end-confirm-icon">📞</div>
            <h3 className="end-confirm-title">Leave call?</h3>
            <p className="end-confirm-body">Are you sure you want to leave this video call?</p>
            <div className="end-confirm-actions">
              <button className="end-confirm-cancel" onClick={() => setShowEndConfirm(false)}>Stay</button>
              <button className="end-confirm-leave" onClick={() => endCall("user_ended")}>Leave</button>
            </div>
          </div>
        </div>
      )}

      {showRejoinPrompt && peerLeftMessage && !showEndConfirm && (
        <div className="peer-left-overlay">
          <div className="peer-left-popup">
            <div className="peer-left-icon">👋</div>
            <h3 className="peer-left-title">Call Ended by Other Participant</h3>
            <p className="peer-left-body">{peerLeftMessage}</p>
            <div className="peer-left-actions">
              <button
                className="end-confirm-cancel"
                onClick={() => {
                  setShowRejoinPrompt(false);
                  setPeerLeftMessage(null);
                  joinRoom();
                }}
              >
                Rejoin
              </button>
              <button
                className="end-confirm-leave"
                onClick={() => {
                  setShowRejoinPrompt(false);
                  endCall("user_ended");
                }}
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {needsMediaUnblock && (
        <div className="media-overlay-fullscreen" style={{ touchAction: "manipulation" }}>
          <div className="media-popup" style={{ touchAction: "manipulation" }}>
            <div className="popup-icon">🔊🎥</div>
            <h2>Enable audio &amp; video</h2>
            <p>Tap the button below to start the call audio and video.</p>
            <button
              className="media-unblock-btn"
              onClick={handleMediaUnblock}
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            >
              Tap to continue
            </button>
            {mediaPermissionDenied && (
              <p className="media-permission-hint">
                Permission was denied. Allow camera &amp; microphone in your browser settings, then refresh.
              </p>
            )}
          </div>
        </div>
      )}

      {isOtpPanelOpen && appointmentId && (
        <OtpPanel
          appointmentId={appointmentId}
          userRole={myRole}
          keyboardOffset={keyboardOffset}
          onClose={() => setIsOtpPanelOpen(false)}
        />
      )}

      {isChatOpen && (
        <div className="chat-drawer">
          <div className="chat-header">
            <div className="chat-header-left">
              <div className={`role-dot ${myRole}`} />
              <span className="chat-title">Chat</span>
            </div>
            <button className="chat-close-btn" onClick={() => setIsChatOpen(false)}>✕</button>
          </div>
          <div className="chat-messages">
            {messages.length === 0 && <div className="chat-empty">No messages yet. Say hello! 👋</div>}
            {messages.map((m) => {
              const mine = isMyMessage(m);
              return (
                <div key={m.id} className={`msg-row ${mine ? "msg-row-mine" : "msg-row-theirs"}`}>
                  <div className="msg-meta">
                    <span className={`msg-sender ${mine ? "sender-mine" : "sender-other"}`}>
                      {getSenderLabel(m)}
                    </span>
                    {m.timestamp && <span className="msg-time">{formatTime(m.timestamp)}</span>}
                  </div>
                  <div className={`msg-bubble ${mine ? "bubble-mine" : "bubble-other"}`}>{m.text}</div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-input-row">
            <input
              className="chat-input"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type a message…"
            />
            <button className="chat-send-btn" onClick={sendMessage}>➤</button>
          </div>
        </div>
      )}

      <div className="status-bar">
        {!isConnected
          ? "🔴 Connecting…"
          : callStatus === "waiting"    ? "🟡 Waiting…"
          : callStatus === "connecting" ? "🟡 Connecting video…"
          : callStatus === "connected"  ? "🟢 Connected"
          : "🔴 Ended"}
      </div>

    </div>
  );
}